import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, patchRound } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Trophy, Snowflake, Eye, Crown, UsersThree, Star, Lock } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  computeIndividualLeaderboard,
  computeIndividualLeaderboardThrough,
  computeRoundLeaderboardNSP,
  computeRoundLeaderboardNSPThrough,
  isRoundFrozen,
  revealHoleForRound,
  displayThru,
  allGroupsCompleted18,
  playingHandicap,
  groupsFinishedSummary,
  teamRoundPoints,
  teamRoundPointsThrough,
  effectiveHolePars,
  formatHandicapIndex,
  formatPlayingHandicap,
  getEffectivePH,
  getAllowedPH,
  displayWHS,
} from '../golf';
import { usePolling } from '../hooks/useForeTour';
import { playerCountingContribution } from '../lib/teamContribution';
import { effectiveCourseForPlayer } from '../lib/effectiveCourse';
import { effectiveStrokeIndexesForPlayer } from '../lib/effectiveCourse';
import { effectiveTeamScoring, roundFormat } from '../lib/formatHelpers';
import { sortedGroupIndexes, groupDisplayName } from '../lib/groupOrder';
import { computeScrambleLeaderboard } from '../lib/scramble';
import { getTripAccess } from '../lib/tripAccess';
import { isRoundFinished } from '../lib/eventStatus';
import EventBranding from '../components/EventBranding';

export default function Leaderboard() {
  const { code, roundId } = useParams();
  const navigate = useNavigate();
  const cacheKey = `foretour:trip:${code}`;
  const [trip, setTrip] = useState(() => {
    try { const c = sessionStorage.getItem(cacheKey); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [round, setRound] = useState(() => trip?.rounds?.find(r => r.id === roundId) || null);
  const [loading, setLoading] = useState(!trip || !round);
  const [showReveal, setShowReveal] = useState(false);
  const [prevEntries, setPrevEntries] = useState(null);
  const [lbView, setLbView] = useState('individual');
  const [revealPinOpen, setRevealPinOpen] = useState(false);
  const [revealPin, setRevealPin] = useState('');
  const [revealPinError, setRevealPinError] = useState('');
  const [divisionTab, setDivisionTab] = useState('overall');

  const etagRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await getTrip(code, etagRef.current);
      if (res.status === 304) return;
      etagRef.current = (res.headers?.etag || '').replace('W/', '') || null;
      setTrip(res.data);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(res.data)); } catch {}
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) setRound(r);
    } catch {} finally { setLoading(false); }
  }, [code, roundId, cacheKey]);

  useEffect(() => { load(); }, [load]);
  const { lastFetchAt } = usePolling(load, 30000, true);

  // Freshness counter — ticks every second
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    setElapsed(0);
    const iv = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(iv);
  }, [lastFetchAt]);

  if (loading) return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="leaderboard-skeleton">
      <div className="h-14 bg-white animate-pulse border-b border-[#E1E6E4]" />
      <div className="px-4 pt-4 max-w-lg mx-auto space-y-3">
        {[1,2,3,4].map(i => <div key={i} className="h-16 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />)}
      </div>
    </div>
  );
  if (!round || !trip) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  let frozen = isRoundFrozen(round);
  const freezeHole = revealHoleForRound(round);
  const allDone = allGroupsCompleted18(round);
  const { finished, total: totalGroups } = groupsFinishedSummary(round);
  const teams = round?.teams || [];
  const players = trip?.players || [];
  const hp = effectiveHolePars(round);
  const fmt = roundFormat(round);
  const isNSP = fmt === 'nsp';
  const showTeams = !isNSP && effectiveTeamScoring(trip, round) && teams.length > 0;
  const isScramble = round?.scoring_mode === 'texas_scramble';
  // For scramble rounds, isRoundFrozen checks empty individual hole_strokes.
  // Override: check team_hole_strokes at the reveal hole position.
  if (!frozen && isScramble && !round.revealed && round.freeze_enabled !== false) {
    for (let t = 0; t < teams.length; t++) {
      const ts = round.team_hole_strokes?.[String(t)] || [];
      if ((ts[freezeHole - 1] ?? 0) > 0) { frozen = true; break; }
    }
  }
  const access = getTripAccess(code);
  const isOrganiser = access?.role === 'organiser' || !!access?.legacy;

  // Scramble leaderboard
  const scrambleStandings = isScramble && trip ? computeScrambleLeaderboard(round, trip) : [];

  // NSP leaderboard (freeze-aware)
  const nspEntriesRaw = isNSP ? (frozen
    ? computeRoundLeaderboardNSPThrough(trip, round, freezeHole - 1, effectiveCourseForPlayer, effectiveStrokeIndexesForPlayer)
    : computeRoundLeaderboardNSP(trip, round, effectiveCourseForPlayer, effectiveStrokeIndexesForPlayer)) : [];
  const nrSet = new Set(round?.nr_players || []);
  const nspEntriesFiltered = nspEntriesRaw.filter(e => !nrSet.has(e.player_id));
  const nspNrEntries = nspEntriesRaw.filter(e => nrSet.has(e.player_id));
  // Re-rank NSP after NR removal
  const nspEntries = nspEntriesFiltered.map((e, idx, arr) => {
    let rank = 1;
    for (let i = 0; i < idx; i++) {
      if (arr[i].net < e.net) rank = i + 2;
      else if (arr[i].net === e.net) rank = arr[i].rank ?? i + 1;
    }
    return { ...e, rank };
  });
  // Proper re-rank: tied players share the same rank
  for (let i = 0; i < nspEntries.length; i++) {
    if (i === 0) { nspEntries[i] = { ...nspEntries[i], rank: 1 }; continue; }
    if (nspEntries[i].net === nspEntries[i-1].net) {
      nspEntries[i] = { ...nspEntries[i], rank: nspEntries[i-1].rank };
    } else {
      nspEntries[i] = { ...nspEntries[i], rank: i + 1 };
    }
  }

  // Individual Stableford leaderboard — scope to THIS round only
  const singleRoundTrip = { ...trip, rounds: round ? [round] : [] };
  const entries = !isNSP ? (frozen
    ? computeIndividualLeaderboardThrough(singleRoundTrip, roundId, freezeHole - 1)
    : computeIndividualLeaderboard(singleRoundTrip)) : [];

  let ranked = entries.filter(e => !nrSet.has(e.player_id));
  const stablefordNrEntries = entries.filter(e => nrSet.has(e.player_id));
  // Re-rank Stableford after NR removal
  ranked.sort((a, b) => b.total - a.total);
  for (let i = 0; i < ranked.length; i++) {
    if (i === 0) { ranked[i] = { ...ranked[i], rank: 1 }; continue; }
    if (ranked[i].total === ranked[i-1].total) {
      ranked[i] = { ...ranked[i], rank: ranked[i-1].rank };
    } else {
      ranked[i] = { ...ranked[i], rank: i + 1 };
    }
  }
  if (frozen) {
    let pos = 1;
    for (let i = 0; i < ranked.length; i++) {
      if (i > 0 && ranked[i].total === ranked[i-1].total) {
        ranked[i] = { ...ranked[i], rank: ranked[i-1].rank };
      } else {
        ranked[i] = { ...ranked[i], rank: pos };
      }
      pos = i + 2;
    }
  }

  // Team leaderboard
  const teamEntries = (() => {
    if (!teams.length) return [];
    if (frozen) {
      return teamRoundPointsThrough(teams, round.hole_scores, hp, freezeHole - 1, round);
    }
    return teamRoundPoints(teams, round.scores || {}, round.hole_scores, hp, round.hole_nr, round);
  })();

  // Compute "Thru X" for a non-scramble team: minimum completed holes across players
  const getTeamThru = (playerIds) => {
    if (!round || !playerIds.length) return 0;
    let min = 18;
    for (const pid of playerIds) {
      const strokes = round.hole_strokes?.[pid] || [];
      const nr = round.hole_nr?.[pid] || [];
      const isRoundNr = (round.nr_players || []).includes(pid);
      let count = 0;
      for (let h = 0; h < 18; h++) {
        if ((strokes[h] || 0) > 0 || nr.includes(h) || isRoundNr) count++;
      }
      min = Math.min(min, count);
    }
    return min;
  };

  // ── Division filtering ──
  const divisions = trip?.divisions || [];
  const divEnabled = trip?.divisions_enabled && round?.use_divisions !== false && !isScramble && divisions.length > 0;
  const playerDivMap = {};
  if (divEnabled) {
    for (const p of players) playerDivMap[p.id] = p.division_id || null;
  }

  const filterByDiv = (entries, divId) => {
    if (!divId || divId === 'overall') return entries;
    const filtered = entries.filter(e => playerDivMap[e.player_id] === divId);
    // Re-rank within division
    const reranked = [...filtered];
    for (let i = 0; i < reranked.length; i++) {
      if (i === 0) { reranked[i] = { ...reranked[i], rank: 1 }; continue; }
      const key = isNSP ? 'net' : 'total';
      const curr = reranked[i][key];
      const prev = reranked[i-1][key];
      if (curr === prev) reranked[i] = { ...reranked[i], rank: reranked[i-1].rank };
      else reranked[i] = { ...reranked[i], rank: i + 1 };
    }
    return reranked;
  };

  const displayNspEntries = divEnabled ? filterByDiv(nspEntries, divisionTab) : nspEntries;
  const displayRanked = divEnabled ? filterByDiv(ranked, divisionTab) : ranked;

  const winnerVisible = showReveal || round?.revealed;

  const handleReveal = async () => {
    // Validate entered PIN matches current trip PIN
    if (revealPin !== trip?.pin) {
      setRevealPinError('Incorrect PIN');
      return;
    }
    setPrevEntries(isNSP ? nspEntries.map(e => ({ ...e })) : ranked.map(e => ({ ...e })));
    try {
      const res = await patchRound(code, roundId, { revealed: true });
      setTrip(res.data);
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) setRound(r);
      setShowReveal(true);
      setRevealPinOpen(false);
    } catch (e) {
      if (e.response?.status === 403) setRevealPinError('Organiser access required');
      else setRevealPinError('Error');
    }
  };

  const getDivBadge = (playerId) => {
    if (!divEnabled || divisionTab !== 'overall') return null;
    const divId = playerDivMap[playerId];
    const div = divId ? divisions.find(d => d.id === divId) : null;
    if (!div) return null;
    return <span className="text-[9px] text-[#8A9BA3] font-semibold ml-1">{div.name}</span>;
  };

  const positionStyle = (pos) => {
    if (pos === 1) return 'bg-[#F9A825] text-[#1D242B]';
    if (pos === 2) return 'bg-[#B8C7C0] text-[#1D242B]';
    if (pos === 3) return 'bg-[#E59000] text-white';
    return 'bg-[#F4F6F5] text-[#455561]';
  };

  const getMovement = (entry) => {
    if (!prevEntries) return 0;
    const prev = prevEntries.find(e => e.player_id === entry.player_id);
    if (!prev) return 0;
    return (prev.rank || 999) - (entry.rank || 999);
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="leaderboard-screen">
      <div className="sticky top-0 z-30">
      <div className="bg-[#1D242B] px-4 lg:px-8 py-4 flex items-center gap-3">
        <button onClick={() => navigate(`/trip/${code}/round/${roundId}/setup`)} className="text-white p-1" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" weight="bold" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl lg:text-2xl text-white font-bold">Leaderboard</h1>
          <p className="text-xs lg:text-sm text-[#B8C7C0]">{round.label} · {round.course?.name}</p>
          <p className="text-[10px] text-[#8A9BA3]" data-testid="freshness-label">
            {isRoundFinished(round, trip) ? 'Final' : `Live · updated ${elapsed}s ago`}
          </p>
        </div>
        {sessionStorage.getItem('ft_scoring_hole') != null && (
          <Button size="sm" variant="ghost" className="text-white bg-white/10 hover:bg-white/20 rounded-lg text-xs"
            onClick={() => {
              navigate(-1);
            }}
            data-testid="back-to-scoring-btn">
            Back to scoring
          </Button>
        )}
        {totalGroups > 0 && <Badge className="bg-[#2C3742] text-[#B8C7C0] text-xs lg:text-sm">{finished}/{totalGroups} groups finished</Badge>}
      </div>

      {/* Frozen banner */}
      {frozen && (
        <div className="bg-[#007AFF] px-4 lg:px-8 py-3" data-testid="frozen-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Snowflake className="w-5 h-5" weight="fill" />
              <span className="text-sm lg:text-base font-semibold">{freezeHole === 0 ? 'Scores hidden until reveal' : `Frozen after hole ${freezeHole}`}</span>
            </div>
            {!revealPinOpen ? (
              <Button size="sm" onClick={() => setRevealPinOpen(true)} className="bg-white text-[#007AFF] h-8 rounded-lg font-bold text-xs" data-testid="reveal-btn">
                <Star className="w-3.5 h-3.5 mr-1 text-[#C9A227]" weight="fill" /><Eye className="w-4 h-4 mr-1" weight="bold" /> Reveal
              </Button>
            ) : (
              <div className="flex items-center gap-1.5" data-testid="reveal-pin-gate">
                <input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  placeholder="PIN" value={revealPin}
                  onChange={(e) => { setRevealPin(e.target.value); setRevealPinError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleReveal()}
                  className="h-8 w-20 rounded-lg border-0 bg-white/90 text-center text-sm font-mono px-2" data-testid="reveal-pin-input" />
                <Button size="sm" onClick={handleReveal} className="bg-white text-[#007AFF] h-8 rounded-lg font-bold text-xs px-3" data-testid="reveal-pin-submit">Go</Button>
                {revealPinError && <span className="text-xs text-[#FFD54F] font-semibold">{revealPinError}</span>}
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      {/* Branding */}
      {trip?.branding?.logo_url && (
        <div className="px-4 lg:px-8 pt-3 max-w-lg mx-auto">
          <EventBranding imageUrl={trip.branding.logo_url} />
        </div>
      )}

      {/* Individual / Teams tabs */}
      {frozen && freezeHole === 0 ? (
        <div className="px-4 lg:px-8 pt-8 max-w-lg mx-auto text-center" data-testid="frozen-from-start-msg">
          <p className="text-[#455561] text-sm">Scores are hidden until the organiser reveals the results.</p>
        </div>
      ) : (
      <div className="px-4 lg:px-8 pt-4 max-w-lg lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
        {isScramble ? (
          <Tabs value="teams">
            <TabsList className="w-full bg-[#F4F6F5] rounded-xl h-11 mb-4">
              <TabsTrigger value="individual" disabled className="flex-1 rounded-lg text-sm lg:text-base font-semibold opacity-40 cursor-not-allowed" data-testid="lb-tab-individual">Individual</TabsTrigger>
              <TabsTrigger value="teams" className="flex-1 rounded-lg text-sm lg:text-base font-semibold data-[state=active]:bg-white data-[state=active]:text-[#1D242B]" data-testid="lb-tab-teams">Teams</TabsTrigger>
            </TabsList>
          </Tabs>
        ) : !isNSP && showTeams ? (
          <Tabs value={lbView} onValueChange={setLbView}>
            <TabsList className="w-full bg-[#F4F6F5] rounded-xl h-11 mb-4">
              <TabsTrigger value="individual" className="flex-1 rounded-lg text-sm lg:text-base font-semibold data-[state=active]:bg-white data-[state=active]:text-[#1D242B]" data-testid="lb-tab-individual">Individual</TabsTrigger>
              <TabsTrigger value="teams" className="flex-1 rounded-lg text-sm lg:text-base font-semibold data-[state=active]:bg-white data-[state=active]:text-[#1D242B]" data-testid="lb-tab-teams">Teams</TabsTrigger>
            </TabsList>
          </Tabs>
        ) : (
          <div className="mb-4">
            <Badge className="bg-[#E8E5DB] text-[#1B4332] text-xs lg:text-sm font-semibold px-2 py-1" data-testid="format-badge">
              {isNSP ? 'Net Stroke Play' : 'Stableford'}{!showTeams && !isNSP ? ' · Individual' : ''}
            </Badge>
            {isNSP && round?.handicap_basis === 'system36' && (
              <Badge className="bg-[#E3F2FD] text-[#1565C0] text-xs lg:text-sm font-semibold px-2 py-1 ml-1" data-testid="s36-badge">System 36</Badge>
            )}
            {isNSP && round?.handicap_basis === 'lower_of' && (
              <Badge className="bg-[#E3F2FD] text-[#1565C0] text-xs lg:text-sm font-semibold px-2 py-1 ml-1" data-testid="lower-of-badge">Lower Of</Badge>
            )}
            {round?.handicap_allowance_pct != null && round.handicap_allowance_pct < 100 && round?.handicap_basis !== 'system36' && (
              <Badge className="bg-[#FFF3E0] text-[#E59000] text-xs lg:text-sm font-semibold px-2 py-1 ml-1" data-testid="allowance-badge">
                Allowance {round.handicap_allowance_pct}%
              </Badge>
            )}
          </div>
        )}

        {/* Division tabs */}
        {divEnabled && !isScramble && (
          <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1" data-testid="division-tabs">
            <button onClick={() => setDivisionTab('overall')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${divisionTab === 'overall' ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'}`}
              data-testid="div-tab-overall">Overall</button>
            {divisions.map(d => (
              <button key={d.id} onClick={() => setDivisionTab(d.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${divisionTab === d.id ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'}`}
                data-testid={`div-tab-${d.id}`}>{d.name}</button>
            ))}
          </div>
        )}

        {/* Individual tab — hidden for scramble */}
        {(lbView === 'individual' || !showTeams) && !isScramble && (
          <>
            {isNSP ? (
              /* ── NSP Individual: tournament-style table ── */
              displayNspEntries.length === 0 ? (
                <Card className="p-8 bg-white border-[#E1E6E4] rounded-xl text-center">
                  <Trophy className="w-10 h-10 text-[#B8C7C0] mx-auto mb-3" />
                  <p className="text-[#455561] text-sm">No scores yet</p>
                </Card>
              ) : (() => {
                const nspRankCounts = {};
                displayNspEntries.forEach(e => { if (e.rank) nspRankCounts[e.rank] = (nspRankCounts[e.rank] || 0) + 1; });
                const nspIsTied = (rank) => nspRankCounts[rank] > 1;
                const nspFormatToPar = (entry) => {
                  if (entry.par_played === 0 || entry.s36_net_pending) return { text: '\u2013', color: 'text-[#455561]' };
                  const diff = entry.net - entry.par_played;
                  if (diff === 0) return { text: 'E', color: 'text-[#1B4332]' };
                  if (diff < 0) return { text: `\u2212${Math.abs(diff)}`, color: 'text-[#C2383B]' };
                  return { text: `+${diff}`, color: 'text-[#1B4332]' };
                };
                return (
                  <div className="max-w-[900px] mx-auto rounded-xl overflow-hidden border border-[#E1E6E4]" data-testid="nsp-individual-table">
                    <div className="flex items-center bg-[#1B4332] text-white text-[10px] font-semibold tracking-wide px-3 py-2">
                      <div className="w-9 flex-shrink-0 text-center">POS</div>
                      <div className="flex-1 min-w-0">PLAYER</div>
                      <div className="w-10 text-center flex-shrink-0">THRU</div>
                      <div className="w-14 text-center flex-shrink-0">TO PAR</div>
                      <div className="w-11 text-center flex-shrink-0">GROSS</div>
                      <div className="w-11 text-center flex-shrink-0">NET</div>
                    </div>
                    <AnimatePresence>
                      {displayNspEntries.map((entry, idx) => {
                        const player = players.find(p => p.id === entry.player_id);
                        const isWinner = winnerVisible && entry.rank === 1;
                        const showWinnerBanner = isWinner && idx === 0;
                        const cappedThru = displayThru(entry.thru, round);
                        const thruLabel = cappedThru >= 18 ? 'F' : cappedThru > 0 ? String(cappedThru) : '\u2013';
                        const posLabel = nspIsTied(entry.rank) ? `T${entry.rank}` : String(entry.rank ?? '');
                        const toPar = nspFormatToPar(entry);
                        const evenRow = idx % 2 === 0;
                        return (
                          <motion.div key={entry.player_id} layout
                            initial={showReveal ? { opacity: 0, y: 20 } : false}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: showReveal ? idx * 0.08 : 0, type: 'spring', stiffness: 120 }}>
                            <div className={`border-t border-[#E1E6E4] ${evenRow ? 'bg-white' : 'bg-[#F7F9F8]'}`}
                              data-testid={`nsp-entry-${entry.player_id}`}>
                              {showWinnerBanner && (
                                <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 px-3 py-1 flex items-center gap-1.5">
                                  <Crown className="w-4 h-4 text-[#D4AF37]" weight="fill" />
                                  <span className="text-[11px] font-bold text-[#D4AF37]">{nspIsTied(1) ? 'Winners' : 'Winner'}</span>
                                </div>
                              )}
                              <div className="flex items-start px-3 py-2">
                                <div className="w-9 flex-shrink-0 text-center pt-0.5">
                                  {entry.par_played === 0 ? (
                                    <span className="text-xs font-bold text-[#455561] tabular-nums">{'\u2013'}</span>
                                  ) : (
                                    <div className="flex items-center justify-center gap-0.5">
                                      {entry.rank === 1 && <span className="w-2 h-2 rounded-full bg-[#D4AF37] inline-block flex-shrink-0" />}
                                      {entry.rank === 2 && <span className="w-2 h-2 rounded-full bg-[#C0C0C0] inline-block flex-shrink-0" />}
                                      {entry.rank === 3 && <span className="w-2 h-2 rounded-full bg-[#B08D57] inline-block flex-shrink-0" />}
                                      <span className="text-xs font-bold text-[#1D242B] tabular-nums">{posLabel}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-sm font-bold text-[#1D242B] break-words">{entry.name}</span>
                                    {getDivBadge(entry.player_id)}
                                    {entry.cb && !frozen && <Badge className="bg-[#007AFF] text-white text-[7px] px-1 py-0 font-bold">CB</Badge>}
                                  </div>
                                  <p className="text-[10px] text-[#8A9BA3] mt-0.5">
                                    {entry.s36_net_pending ? (
                                      <>S36 – · Gross {entry.gross || '–'}</>
                                    ) : round?.handicap_basis === 'lower_of' && entry.basis_source ? (
                                      <>
                                        <span className={entry.basis_source === 'entered_at_allowance' ? 'font-black text-[#1B4332]' : ''}>PH {formatPlayingHandicap(entry.allowed_entered_ph ?? entry.handicap)}</span>
                                        {' · '}
                                        <span className={entry.basis_source === 's36' ? 'font-black text-[#1B4332]' : ''}>S36 {formatPlayingHandicap(entry.s36_handicap)}</span>
                                      </>
                                    ) : round?.handicap_basis === 'system36' && entry.s36_handicap != null ? (
                                      <>S36 {formatPlayingHandicap(entry.s36_handicap)}</>
                                    ) : (
                                      <>PH {formatPlayingHandicap(entry.handicap)} · WHS {displayWHS(player)}</>
                                    )}
                                  </p>
                                </div>
                                <div className="w-10 text-center flex-shrink-0 pt-0.5"><span className="text-xs text-[#455561] tabular-nums font-medium">{thruLabel}</span></div>
                                <div className="w-14 text-center flex-shrink-0 pt-0.5"><span className={`text-base font-black tabular-nums ${toPar.color}`}>{toPar.text}</span></div>
                                <div className="w-11 text-center flex-shrink-0 pt-0.5"><span className="text-xs text-[#455561] tabular-nums font-medium">{entry.par_played === 0 ? '\u2013' : entry.gross}</span></div>
                                <div className="w-11 text-center flex-shrink-0 pt-0.5"><span className="text-xs text-[#455561] tabular-nums font-medium">{entry.par_played === 0 || entry.s36_net_pending ? '\u2013' : entry.net}</span></div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                    {nspNrEntries.length > 0 && nspNrEntries.map(entry => (
                      <div key={entry.player_id} className="border-t border-[#E1E6E4] bg-white/50 opacity-50" data-testid={`nr-entry-${entry.player_id}`}>
                        <div className="flex items-center px-3 py-2">
                          <div className="w-9 flex-shrink-0 text-center"><span className="text-[10px] font-bold text-[#B8C7C0]">NR</span></div>
                          <div className="flex-1 min-w-0"><span className="text-sm text-[#455561]">{entry.name}</span></div>
                          <span className="text-sm font-bold text-[#B8C7C0] tabular-nums">NR</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : (
              /* ── Stableford Individual: tournament-style table ── */
              displayRanked.length === 0 ? (
                <Card className="p-8 bg-white border-[#E1E6E4] rounded-xl text-center">
                  <Trophy className="w-10 h-10 text-[#B8C7C0] mx-auto mb-3" />
                  <p className="text-[#455561] text-sm">No scores yet</p>
                </Card>
              ) : (() => {
                const sfRankCounts = {};
                displayRanked.forEach(e => { if (e.rank) sfRankCounts[e.rank] = (sfRankCounts[e.rank] || 0) + 1; });
                const sfIsTied = (rank) => sfRankCounts[rank] > 1;
                return (
                  <div className="max-w-[900px] mx-auto">
                    <div className="rounded-xl overflow-hidden border border-[#E1E6E4]" data-testid="sf-individual-table">
                      <div className="flex items-center bg-[#1B4332] text-white text-[10px] font-semibold tracking-wide px-3 py-2">
                        <div className="w-9 flex-shrink-0 text-center">POS</div>
                        <div className="flex-1 min-w-0">PLAYER</div>
                        <div className="w-10 text-center flex-shrink-0">THRU</div>
                        <div className="w-14 text-center flex-shrink-0">PTS</div>
                      </div>
                      <AnimatePresence>
                        {displayRanked.map((entry, idx) => {
                          const player = players.find(p => p.id === entry.player_id);
                          const ph = player && round ? getAllowedPH(player, effectiveCourseForPlayer(round, player.id), round) : 0;
                          const movement = getMovement(entry);
                          const hs = round?.hole_strokes?.[entry.player_id] || [];
                          const isWinner = winnerVisible && entry.rank === 1;
                          const playerNrSet = new Set(round?.hole_nr?.[entry.player_id] || []);
                          const holesPlayed = hs.filter((v, i) => v > 0 || playerNrSet.has(i)).length;
                          const showWinnerBanner = isWinner && idx === 0;
                          const cappedHP = displayThru(holesPlayed, round);
                          const thruLabel = cappedHP === 0 ? '\u2013' : cappedHP >= 18 ? 'F' : String(cappedHP);
                          const posLabel = sfIsTied(entry.rank) ? `T${entry.rank}` : String(entry.rank ?? '');
                          const evenRow = idx % 2 === 0;
                          return (
                            <motion.div key={entry.player_id} layout
                              initial={showReveal ? { opacity: 0, y: 20 } : false}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5, delay: showReveal ? idx * 0.08 : 0, type: 'spring', stiffness: 120 }}>
                              <div className={`border-t border-[#E1E6E4] ${evenRow ? 'bg-white' : 'bg-[#F7F9F8]'}`}
                                data-testid={`lb-entry-${entry.player_id}`}>
                                {showWinnerBanner && (
                                  <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 px-3 py-1 flex items-center gap-1.5">
                                    <Crown className="w-4 h-4 text-[#D4AF37]" weight="fill" />
                                    <span className="text-[11px] font-bold text-[#D4AF37]">{sfIsTied(1) ? 'Winners' : 'Winner'}</span>
                                  </div>
                                )}
                                <div className="flex items-start px-3 py-2">
                                  <div className="w-9 flex-shrink-0 text-center pt-0.5">
                                    {holesPlayed === 0 ? (
                                      <span className="text-xs font-bold text-[#455561] tabular-nums">{'\u2013'}</span>
                                    ) : (
                                      <div className="flex items-center justify-center gap-0.5">
                                        {entry.rank === 1 && <span className="w-2 h-2 rounded-full bg-[#D4AF37] inline-block flex-shrink-0" />}
                                        {entry.rank === 2 && <span className="w-2 h-2 rounded-full bg-[#C0C0C0] inline-block flex-shrink-0" />}
                                        {entry.rank === 3 && <span className="w-2 h-2 rounded-full bg-[#B08D57] inline-block flex-shrink-0" />}
                                        <span className="text-xs font-bold text-[#1D242B] tabular-nums">{posLabel}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-sm font-bold text-[#1D242B] break-words">{entry.name}</span>
                                      {getDivBadge(entry.player_id)}
                                      {entry.cb && !frozen && <Badge className="bg-[#007AFF] text-white text-[7px] px-1 py-0 font-bold" data-testid={`cb-chip-${entry.player_id}`}>CB</Badge>}
                                      {showReveal && movement !== 0 && (
                                        <motion.span initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
                                          className={`text-[9px] font-bold px-1 py-0 rounded ${movement > 0 ? 'bg-[#E8E5DB] text-[#1B4332]' : 'bg-red-50 text-[#D32F2F]'}`}>
                                          {movement > 0 ? `+${movement}` : movement}
                                        </motion.span>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-[#8A9BA3] mt-0.5">PH {formatPlayingHandicap(ph)} · WHS {displayWHS(player)}</p>
                                  </div>
                                  <div className="w-10 text-center flex-shrink-0 pt-0.5"><span className="text-xs text-[#455561] tabular-nums font-medium">{thruLabel}</span></div>
                                  <div className="w-14 text-center flex-shrink-0 pt-0.5"><span className="text-base font-black text-[#1D242B] tabular-nums">{entry.total}</span></div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                    {stablefordNrEntries.length > 0 && (
                      <div className="mt-2 rounded-xl overflow-hidden border border-[#E1E6E4]">
                        {stablefordNrEntries.map(entry => (
                          <div key={entry.player_id} className="border-t first:border-t-0 border-[#E1E6E4] bg-white/50 opacity-50" data-testid={`nr-entry-${entry.player_id}`}>
                            <div className="flex items-center px-3 py-2">
                              <div className="w-9 flex-shrink-0 text-center"><span className="text-[10px] font-bold text-[#B8C7C0]">NR</span></div>
                              <div className="flex-1 min-w-0"><span className="text-sm text-[#455561]">{entry.name}</span></div>
                              <span className="text-sm font-bold text-[#B8C7C0] tabular-nums">NR</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </>
        )}

        {/* Scramble Teams leaderboard — tournament-style compact table */}
        {isScramble && (
          <>
            {scrambleStandings.length === 0 ? (
              <Card className="p-8 bg-white border-[#E1E6E4] rounded-xl text-center" data-testid="no-scramble-scores">
                <Trophy className="w-10 h-10 text-[#B8C7C0] mx-auto mb-3" />
                <p className="text-[#455561] text-sm">No team scores yet</p>
              </Card>
            ) : (() => {
              // Detect ties: a rank is tied if any other team shares the same rank
              const rankCounts = {};
              scrambleStandings.forEach(st => { rankCounts[st.rank] = (rankCounts[st.rank] || 0) + 1; });
              const isTied = (rank) => rankCounts[rank] > 1;

              const formatToPar = (net, parPlayed, thru) => {
                if (thru === 0) return { text: '\u2013', color: 'text-[#455561]' };
                const diff = net - parPlayed;
                if (diff === 0) return { text: 'E', color: 'text-[#1B4332]' };
                if (diff < 0) return { text: `\u2212${Math.abs(diff)}`, color: 'text-[#C2383B]' };
                return { text: `+${diff}`, color: 'text-[#1B4332]' };
              };

              return (
                <div className="max-w-[900px] mx-auto rounded-xl overflow-hidden border border-[#E1E6E4]" data-testid="scramble-table">
                  {/* Header row */}
                  <div className="flex items-center bg-[#1B4332] text-white text-[10px] font-semibold tracking-wide px-3 py-2">
                    <div className="w-9 flex-shrink-0 text-center">POS</div>
                    <div className="flex-1 min-w-0">TEAM</div>
                    <div className="w-10 text-center flex-shrink-0">THRU</div>
                    {isNSP ? (
                      <>
                        <div className="w-14 text-center flex-shrink-0">TO PAR</div>
                        <div className="w-11 text-center flex-shrink-0">GROSS</div>
                        <div className="w-11 text-center flex-shrink-0">NET</div>
                      </>
                    ) : (
                      <div className="w-14 text-center flex-shrink-0">PTS</div>
                    )}
                  </div>

                  {/* Team rows */}
                  {scrambleStandings.map((st, idx) => {
                    const isWinner = winnerVisible && st.rank === 1;
                    const showWinnerBanner = isWinner && idx === 0;
                    const cappedThruS = displayThru(st.thru, round);
                    const thruLabel = cappedThruS >= 18 ? 'F' : cappedThruS > 0 ? String(cappedThruS) : '\u2013';
                    const posLabel = isTied(st.rank) ? `T${st.rank}` : String(st.rank);
                    const toPar = isNSP ? formatToPar(st.total, st.par_played, st.thru) : null;
                    const evenRow = idx % 2 === 0;

                    return (
                      <div key={st.team_index}
                        className={`border-t border-[#E1E6E4] ${evenRow ? 'bg-white' : 'bg-[#F7F9F8]'}`}
                        data-testid={`scramble-team-${st.team_index}`}>
                        {showWinnerBanner && (
                          <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 px-3 py-1 flex items-center gap-1.5">
                            <Crown className="w-4 h-4 text-[#D4AF37]" weight="fill" />
                            <span className="text-[11px] font-bold text-[#D4AF37]">{isTied(1) ? 'Winners' : 'Winner'}</span>
                          </div>
                        )}
                        {/* Score row */}
                        <div className="flex items-start px-3 py-2">
                          <div className="w-9 flex-shrink-0 text-center pt-0.5">
                            {st.par_played === 0 ? (
                              <span className="text-xs font-bold text-[#455561] tabular-nums">{'\u2013'}</span>
                            ) : (
                              <div className="flex items-center justify-center gap-0.5">
                                {st.rank === 1 && <span className="w-2 h-2 rounded-full bg-[#D4AF37] inline-block flex-shrink-0" />}
                                {st.rank === 2 && <span className="w-2 h-2 rounded-full bg-[#C0C0C0] inline-block flex-shrink-0" />}
                                {st.rank === 3 && <span className="w-2 h-2 rounded-full bg-[#B08D57] inline-block flex-shrink-0" />}
                                <span className="text-xs font-bold text-[#1D242B] tabular-nums">{posLabel}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-bold text-[#1D242B]">{st.team_name}</span>
                              <span className="text-[10px] text-[#8A9BA3]">PH {st.team_ph}</span>
                              {st.cb && !frozen && <Badge className="bg-[#007AFF] text-white text-[7px] px-1 py-0 font-bold">CB</Badge>}
                              {st.drives_under_min && isOrganiser && (
                                <Badge className="bg-[#FFEBEE] text-[#D32F2F] text-[7px] px-1 py-0 font-bold" data-testid={`under-min-${st.team_index}`}>Low drives</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-[#8A9BA3] mt-0.5 leading-relaxed">
                              {(round?.teams?.[st.team_index] || []).map((pid, pi, arr) => {
                                const p = players.find(pl => pl.id === pid);
                                if (!p) return null;
                                const isLast = pi === arr.length - 1 || arr.slice(pi + 1).every(id => !players.find(pl => pl.id === id));
                                return <span key={pid} className="whitespace-nowrap">{p.name}{!isLast ? ' ·' : ''}</span>;
                              })}
                            </div>
                          </div>
                          <div className="w-10 text-center flex-shrink-0 pt-0.5">
                            <span className="text-xs text-[#455561] tabular-nums font-medium">{thruLabel}</span>
                          </div>
                          {isNSP ? (
                            <>
                              <div className="w-14 text-center flex-shrink-0 pt-0.5">
                                <span className={`text-base font-black tabular-nums ${toPar.color}`}>{toPar.text}</span>
                              </div>
                              <div className="w-11 text-center flex-shrink-0 pt-0.5">
                                <span className="text-xs text-[#455561] tabular-nums font-medium">{st.thru === 0 ? '\u2013' : st.gross}</span>
                              </div>
                              <div className="w-11 text-center flex-shrink-0 pt-0.5">
                                <span className="text-xs text-[#455561] tabular-nums font-medium">{st.thru === 0 ? '\u2013' : st.total}</span>
                              </div>
                            </>
                          ) : (
                            <div className="w-14 text-center flex-shrink-0 pt-0.5">
                              <span className="text-base font-black text-[#1D242B] tabular-nums">{st.thru === 0 ? '\u2013' : st.total}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </>
        )}

        {/* Teams tab (individual team scoring — NOT scramble) */}
        {lbView === 'teams' && showTeams && !isScramble && (
          <>
            {teams.length === 0 ? (
              <Card className="p-8 bg-white border-[#E1E6E4] rounded-xl text-center" data-testid="no-teams-msg">
                <UsersThree className="w-10 h-10 text-[#B8C7C0] mx-auto mb-3" />
                <p className="text-[#455561] text-sm mb-3">No teams drawn yet — set up in Team Draw</p>
                <Button size="sm" onClick={() => navigate(`/trip/${code}/round/${roundId}/teams`)}
                  className="bg-[#1B4332] text-white rounded-xl" data-testid="go-team-draw-btn">Go to Team Draw</Button>
              </Card>
            ) : teamEntries.length === 0 ? (
              <div className="space-y-3">
                {sortedGroupIndexes(round).map(idx => {
                  const teamPlayers = teams[idx];
                  return (
                  <Card key={idx} className="bg-white border-[#E1E6E4] rounded-xl p-4" data-testid={`lb-team-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-[#1D242B]">{groupDisplayName(round, idx)}</span>
                      <span className="text-sm text-[#B8C7C0]">0 pts</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {teamPlayers.map(pid => {
                        const p = players.find(pl => pl.id === pid);
                        return p ? <Badge key={pid} className="bg-[#F4F6F5] text-[#455561] text-xs">{p.name}</Badge> : null;
                      })}
                    </div>
                  </Card>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {teamEntries.map((te, idx) => {
                  const contribCap = frozen ? freezeHole - 1 : 17;
                  if (process.env.NODE_ENV !== 'production') {
                    const sumContrib = te.player_ids.reduce(
                      (s, pid) => s + playerCountingContribution(round, te.player_ids, pid, contribCap, te.team_index), 0
                    );
                    if (Math.abs(sumContrib - te.stableford_total) > 0.001) {
                      console.warn(`Team ${te.team_index} contributions ${sumContrib} != team total ${te.stableford_total}`);
                    }
                  }
                  return (
                  <Card key={te.team_index}
                    className={`bg-white border-[#E1E6E4] rounded-xl overflow-hidden ${te.rank <= 3 ? 'border-l-4 border-l-[#1B4332]' : ''}`}
                    data-testid={`lb-team-${te.team_index}`}>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${positionStyle(te.rank)}`}>{te.rank}</div>
                          <span className="font-semibold text-[#1D242B] lg:text-lg">{groupDisplayName(round, te.team_index)}</span>
                          {te.cb && <Badge className="bg-[#007AFF] text-white text-[8px] lg:text-xs px-1 py-0 font-bold" data-testid={`team-cb-${te.team_index}`}>CB</Badge>}
                        </div>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const rawThru = getTeamThru(te.player_ids);
                            const capped = displayThru(rawThru, round);
                            const label = capped >= 18 ? 'F' : capped > 0 ? String(capped) : '\u2013';
                            return <span className="text-xs text-[#455561] tabular-nums font-medium" data-testid={`team-thru-${te.team_index}`}>{label}</span>;
                          })()}
                          <span className="text-xl lg:text-2xl font-bold text-[#1B4332] tabular-nums">{te.stableford_total} <span className="text-xs lg:text-sm text-[#455561] font-normal">pts</span></span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {te.player_ids.map(pid => {
                          const p = players.find(pl => pl.id === pid);
                          const contribution = playerCountingContribution(round, te.player_ids, pid, contribCap, te.team_index);
                          return p ? (
                            <div key={pid} className="flex items-center gap-1.5 bg-[#F4F6F5] rounded-lg px-2.5 py-1" data-testid={`team-player-pts-${pid}`}>
                              <span className="text-xs lg:text-sm text-[#1D242B] font-medium truncate max-w-[90px] lg:max-w-[160px]">{p.name}</span>
                              <span className="text-xs lg:text-sm font-bold text-[#1B4332] tabular-nums flex-shrink-0 whitespace-nowrap">{contribution} pts</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      )}
    </div>
  );
}
